import { useEffect, useRef, useState } from 'react';
import { FormControlLabel, Stack, Switch } from '@mui/material';
import NumberSpinner from '@/components/common/number-spinner';
import { useTranslations } from '@/i18n/use-translations';
import {
  DWELL_PERSIST_DEBOUNCE_MS,
  MAX_DWELL_SECONDS,
  MIN_DWELL_SECONDS,
  dwellMsToSeconds,
  dwellSecondsToMs,
} from '@/helpers/sequenced-playback.helper';

export type SequencedPlaybackFormValues = {
  loopEnabled: boolean;
  dwellMs: number;
};

type SequencedContentEditorProps = {
  value: SequencedPlaybackFormValues;
  onChange: (next: SequencedPlaybackFormValues) => void;
  disabled?: boolean;
};

export function SequencedContentEditor({ value, onChange, disabled = false }: SequencedContentEditorProps) {
  const t = useTranslations();
  const [localValue, setLocalValue] = useState(value);
  const localValueRef = useRef(localValue);
  const persistDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    localValueRef.current = localValue;
  }, [localValue]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const clearPersistDebounce = () => {
    if (persistDebounceRef.current) {
      clearTimeout(persistDebounceRef.current);
      persistDebounceRef.current = null;
    }
  };

  const persistNow = (next: SequencedPlaybackFormValues) => {
    clearPersistDebounce();
    onChangeRef.current(next);
  };

  const scheduleDwellPersist = (next: SequencedPlaybackFormValues) => {
    clearPersistDebounce();
    persistDebounceRef.current = setTimeout(() => {
      persistDebounceRef.current = null;
      onChangeRef.current(next);
    }, DWELL_PERSIST_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (persistDebounceRef.current) {
      return;
    }
    setLocalValue(value);
  }, [value.loopEnabled, value.dwellMs]);

  useEffect(
    () => () => {
      if (persistDebounceRef.current) {
        clearTimeout(persistDebounceRef.current);
        onChangeRef.current(localValueRef.current);
      }
    },
    [],
  );

  const dwellSeconds = dwellMsToSeconds(localValue.dwellMs);
  const spinnerDisabled = disabled || !localValue.loopEnabled;

  return (
    <Stack
      direction="row"
      alignItems="flex-end"
      justifyContent="space-between"
      spacing={2}
      sx={{ px: 1, py: 0.5, width: '100%' }}
    >
      <FormControlLabel
        sx={{ mr: 0, flexShrink: 0, mb: 0.25 }}
        control={
          <Switch
            checked={localValue.loopEnabled}
            disabled={disabled}
            onChange={(event) => {
              const next = { ...localValueRef.current, loopEnabled: event.target.checked };
              setLocalValue(next);
              persistNow(next);
            }}
          />
        }
        label={t('media.sequencedContent.autoLoop')}
      />
      <NumberSpinner
        label={t('media.sequencedContent.dwellInSeconds')}
        size="small"
        min={MIN_DWELL_SECONDS}
        max={MAX_DWELL_SECONDS}
        step={1}
        value={dwellSeconds}
        disabled={spinnerDisabled}
        onValueChange={(nextSeconds) => {
          if (nextSeconds == null) {
            return;
          }
          const next = {
            ...localValueRef.current,
            dwellMs: dwellSecondsToMs(nextSeconds),
          };
          setLocalValue(next);
          scheduleDwellPersist(next);
        }}
      />
    </Stack>
  );
}
